import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import {
  Box, Typography, Paper, TextField, Button, Chip, Avatar,
  Divider, Alert, CircularProgress, IconButton, Card, CardContent,
  CardMedia, Grid, Snackbar, Link, Dialog, DialogContent, DialogTitle, FormControl,
  Select, MenuItem, InputLabel
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import LockIcon from '@mui/icons-material/Lock';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import axios from 'axios';
import { API_URL } from './constants';

const TicketDetailView = () => {
  const { ticketId, projectKey } = useParams();
  const { projectKey: authProjectKey, isAdmin } = useAuth();
  const navigate = useNavigate();
  const currentProjectKey = projectKey || authProjectKey;
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [newFiles, setNewFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [previewImageName, setPreviewImageName] = useState('');
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingMessageText, setEditingMessageText] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [timeEstEarliestDate, setTimeEstEarliestDate] = useState(null);
  const [timeEstLatestDate, setTimeEstLatestDate] = useState(null);
  const [savingTimeEst, setSavingTimeEst] = useState(false);
  const [statusValue, setStatusValue] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!projectKey && authProjectKey && ticketId) {
      navigate(`/${authProjectKey}/support/${ticketId}${window.location.search || ''}`, { replace: true });
    }
  }, [projectKey, authProjectKey, ticketId, navigate]);

  useEffect(() => {
    fetchTicketDetail();
  }, [ticketId]);

  useEffect(() => {
    if (ticket) {
      setStatusValue(ticket.status || 'Open');
    }
  }, [ticket]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchTicketDetail = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/support/${ticketId}`, {
        withCredentials: true
      });
      setTicket(response.data);
      setMessages(response.data.messages || []);
      setCurrentUserId(response.data.current_user_id || null);
      // Sort attachments by creation time (oldest first)
      const sortedAttachments = (response.data.attachments || []).sort((a, b) => {
        const dateA = new Date(a.created_at);
        const dateB = new Date(b.created_at);
        return dateA - dateB;
      });
      setAttachments(sortedAttachments);
    } catch (error) {
      console.error('Error fetching ticket detail:', error);
      setError('Failed to load ticket details');
    } finally {
      setLoading(false);
    }
  };

  // Create a combined timeline of messages and attachments sorted by creation time
  const getTimelineItems = () => {
    const items = [];
    
    // Add initial ticket message
    if (ticket && ticket.created_at) {
      items.push({
        type: 'message',
        id: `ticket-${ticket.id}`,
        data: ticket,
        created_at: ticket.created_at,
        author_name: ticket.author_name,
        author_picture: ticket.author_picture,
        isInitial: true
      });
    }
    
    // Add messages
    messages.forEach(msg => {
      items.push({
        type: 'message',
        id: `msg-${msg.id}`,
        data: msg,
        created_at: msg.created_at,
        author_name: msg.author_name,
        author_picture: msg.author_picture,
        isInitial: false
      });
    });
    
    // Add attachments
    attachments.forEach(att => {
      items.push({
        type: 'attachment',
        id: `att-${att.id}`,
        data: att,
        created_at: att.created_at,
        author_name: att.author_name,
        author_picture: att.author_picture,
        isInitial: false
      });
    });
    
    // Sort by creation time
    return items.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateA - dateB;
    });
  };

  const handlePaste = async (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;
    
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
    const imageFiles = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const blob = item.getAsFile();
        if (blob && blob.size <= MAX_FILE_SIZE) {
          const file = new File([blob], `pasted-image-${Date.now()}.png`, { type: blob.type });
          imageFiles.push(file);
        } else if (blob && blob.size > MAX_FILE_SIZE) {
          setUploadError('Pasted image exceeds 25 MB limit');
        }
      }
    }
    
    if (imageFiles.length > 0) {
      setNewFiles(prev => [...prev, ...imageFiles]);
      setUploadError('');
    }
  };

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB
    
    const validFiles = [];
    const errors = [];
    
    selectedFiles.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} exceeds 25 MB limit`);
      } else {
        validFiles.push(file);
      }
    });
    
    if (errors.length > 0) {
      setUploadError(errors.join(', '));
    } else {
      setUploadError('');
    }
    
    setNewFiles(prev => [...prev, ...validFiles]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setNewFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const isImageFile = (mimeType, filename) => {
    const imageMimeTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
    return imageMimeTypes.includes(mimeType?.toLowerCase()) || 
           imageExtensions.some(ext => filename?.toLowerCase().endsWith(ext));
  };

  const getImageUrl = (attachmentId) => {
    return `${API_URL}/support/${ticketId}/attachments/${attachmentId}`;
  };

  const handleImageClick = (attachmentId, filename) => {
    setPreviewImageUrl(getImageUrl(attachmentId));
    setPreviewImageName(filename);
    setImagePreviewOpen(true);
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && newFiles.length === 0) return;

    try {
      setSending(true);
      
      // Send message if there's text
      if (newMessage.trim()) {
        const messageResponse = await axios.post(
          `${API_URL}/support/${ticketId}/messages`,
          { message: newMessage },
          { withCredentials: true }
        );
        setMessages(prev => [...prev, messageResponse.data]);
        setNewMessage('');
      }
      
      // Upload files if any
      if (newFiles.length > 0) {
        const uploadPromises = newFiles.map(file => {
          const formData = new FormData();
          formData.append('file', file);
          return axios.post(
            `${API_URL}/support/${ticketId}/attachments`,
            formData,
            {
              withCredentials: true,
              headers: { 'Content-Type': 'multipart/form-data' }
            }
          );
        });
        
        await Promise.all(uploadPromises);
        await fetchTicketDetail(); // Refresh to get updated attachments
        setNewFiles([]);
      }
      
      setSuccess('Message sent successfully');
    } catch (error) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await axios.get(
        `${API_URL}/support/${ticketId}/attachments/${attachmentId}`,
        {
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setError('Failed to download file');
    }
  };

  const handleEditMessage = (message) => {
    setEditingMessageId(message.id);
    setEditingMessageText(message.message);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingMessageText('');
  };

  const handleSaveEdit = async () => {
    if (!editingMessageText.trim()) return;

    try {
      const response = await axios.put(
        `${API_URL}/support/${ticketId}/messages/${editingMessageId}`,
        { message: editingMessageText },
        { withCredentials: true }
      );
      
      setMessages(prev => prev.map(msg => 
        msg.id === editingMessageId ? response.data : msg
      ));
      setEditingMessageId(null);
      setEditingMessageText('');
      setSuccess('Message updated successfully');
    } catch (error) {
      console.error('Error updating message:', error);
      setError('Failed to update message');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) {
      return;
    }

    try {
      await axios.delete(
        `${API_URL}/support/${ticketId}/messages/${messageId}`,
        { withCredentials: true }
      );
      
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      setSuccess('Message deleted successfully');
    } catch (error) {
      console.error('Error deleting message:', error);
      setError('Failed to delete message');
    }
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  /** Format time estimate: calculate day/hour range from completion dates. */
  const formatTimeEstimate = (eta, opts = {}) => {
    const status = opts.status;
    const statusLower = (status || '').toLowerCase();
    
    // If resolved or closed, show "-"
    if (statusLower === 'resolved' || statusLower === 'closed') {
      return '-';
    }
    
    const earliestDate = opts.earliest_completion_date;
    const latestDate = opts.latest_completion_date;
    
    if (!earliestDate && !latestDate) {
      if (eta == null || eta === '') return 'Not set';
      const date = new Date(eta);
      if (isNaN(date.getTime())) return 'Not set';
      const now = new Date();
      const diffMs = date - now;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      // If overdue (negative hours), show "due X hours"
      if (diffHours < 0) {
        const hoursOverdue = Math.abs(Math.floor(diffHours));
        return `due ${hoursOverdue} hours`;
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      date.setHours(0, 0, 0, 0);
      const diffDaysMs = date - today;
      const days = Math.ceil(diffDaysMs / (24 * 60 * 60 * 1000));
      if (days === 0) return 'Today';
      if (days === 1) return 'in 1 day';
      return `in ${days} days`;
    }
    
    try {
      const now = new Date();
      let earliest, latest;
      
      if (earliestDate) {
        earliest = new Date(earliestDate);
      } else {
        return 'Not set';
      }
      
      if (latestDate) {
        latest = new Date(latestDate);
      } else {
        latest = earliest;
      }
      
      // Calculate hours from now to earliest and latest
      const earliestFromNowMs = earliest - now;
      const latestFromNowMs = latest - now;
      const earliestFromNowHours = earliestFromNowMs / (1000 * 60 * 60);
      const latestFromNowHours = latestFromNowMs / (1000 * 60 * 60);
      
      // If overdue (negative hours) and not resolved/closed, show "due X hours"
      if (earliestFromNowHours < 0) {
        const hoursOverdue = Math.abs(Math.floor(earliestFromNowHours));
        return `due ${hoursOverdue} hours`;
      }
      
      // If less than 24 hours remaining, show hours range (0-23 hours)
      if (earliestFromNowHours < 24 && earliestFromNowHours >= 0) {
        const minHours = Math.max(0, Math.floor(earliestFromNowHours));
        const maxHours = Math.min(23, Math.ceil(latestFromNowHours));
        if (minHours === maxHours) {
          return `in ${minHours} hours`;
        }
        return `in ${minHours} to ${maxHours} hours`;
      }
      
      // Calculate calendar days from now
      const nowDateOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const earliestDateOnly = new Date(earliest.getFullYear(), earliest.getMonth(), earliest.getDate());
      const latestDateOnly = new Date(latest.getFullYear(), latest.getMonth(), latest.getDate());
      const earliestDays = Math.ceil((earliestDateOnly - nowDateOnly) / (1000 * 60 * 60 * 24));
      const latestDays = Math.ceil((latestDateOnly - nowDateOnly) / (1000 * 60 * 60 * 24));
      
      if (earliestDays < 0) {
        // Calculate hours overdue
        const hoursOverdue = Math.abs(Math.floor(earliestFromNowHours));
        return `due ${hoursOverdue} hours`;
      }
      if (earliestDays === latestDays) {
        if (earliestDays === 0) return 'Today';
        if (earliestDays === 1) return 'in 1 day';
        return `in ${earliestDays} days`;
      }
      return `in ${earliestDays} to ${latestDays} days`;
    } catch {
      return 'Not set';
    }
  };

  const handleSaveTimeEstimate = async () => {
    const payload = {};
    
    if (timeEstEarliestDate) {
      payload.earliest_completion_date = timeEstEarliestDate.format('YYYY-MM-DD HH:mm:ss');
    }
    
    if (timeEstLatestDate) {
      payload.latest_completion_date = timeEstLatestDate.format('YYYY-MM-DD HH:mm:ss');
    }
    
    if (Object.keys(payload).length === 0) {
      setError('Set at least one completion date');
      return;
    }
    
    setSavingTimeEst(true);
    try {
      await axios.put(`${API_URL}/support/${ticketId}`, payload, { withCredentials: true });
      await fetchTicketDetail();
      setSuccess('Time estimate updated and added to discussion');
      setTimeEstEarliestDate(null);
      setTimeEstLatestDate(null);
    } catch (err) {
      console.error('Error saving time estimate:', err);
      setError('Failed to save time estimate');
    } finally {
      setSavingTimeEst(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusValue(newStatus);
    setSavingStatus(true);
    try {
      await axios.put(`${API_URL}/support/${ticketId}`, { status: newStatus }, { withCredentials: true });
      await fetchTicketDetail();
      setSuccess('Status updated');
    } catch (err) {
      console.error('Error updating status:', err);
      setError('Failed to update status');
      // Revert status on error
      setStatusValue(ticket.status || 'Open');
    } finally {
      setSavingStatus(false);
    }
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    if (statusLower === 'open') return 'warning'; // Yellow
    if (statusLower === 'in progress') return 'info'; // Blue
    if (statusLower === 'waiting for user') return 'default'; // Gray
    if (statusLower === 'resolved') return 'success'; // Green
    if (statusLower === 'closed') return 'default'; // White/Gray
    return 'default'; // Default fallback
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
        <Alert severity="error">Ticket not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => {
          const basePath = currentProjectKey ? `/${currentProjectKey}/support` : '/support';
          navigate(basePath);
        }} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
          {ticket.is_private && <LockIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />}
          <Typography variant="h4" noWrap sx={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }} title={ticket.subject || 'Support Ticket'}>
            {ticket.subject || 'Support Ticket'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Snackbar
          open={!!success}
          autoHideDuration={3000}
          onClose={() => setSuccess('')}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity="success" onClose={() => setSuccess('')}>
            {success}
          </Alert>
        </Snackbar>
      )}
      {uploadError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError('')}>
          {uploadError}
        </Alert>
      )}

      {/* Ticket Info */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            {isAdmin ? (
              <FormControl fullWidth sx={{ mb: 2, minWidth: 200 }}>
                <Select
                  value={statusValue}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={savingStatus}
                  size="small"
                >
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="In Progress">In Progress</MenuItem>
                  <MenuItem value="Resolved">Resolved</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <Chip 
                label={ticket.status || 'Open'} 
                color={getStatusColor(ticket.status)}
                sx={{ mb: 2 }}
              />
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Author</Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>{ticket.author_name || '-'}</Typography>
          </Grid>
          {ticket.is_private && (
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" color="text.secondary">Visibility</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  Private (only you & support staff)
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">Time estimate</Typography>
            <Box sx={{ mb: 2 }}>
              <Chip
                label={formatTimeEstimate(ticket.eta, {
                  earliest_completion_date: ticket.earliest_completion_date,
                  latest_completion_date: ticket.latest_completion_date,
                  status: ticket.status
                })}
                size="small"
                color={ticket.earliest_completion_date || ticket.latest_completion_date ? 'primary' : 'default'}
                variant="outlined"
              />
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">Description</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
              {ticket.message}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Admin: Set time estimate (datetime range with hours) */}
      {isAdmin && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Set time estimate (admin)</Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <DateTimePicker
                    label="Earliest completion date & time"
                    value={timeEstEarliestDate}
                    onChange={(newValue) => setTimeEstEarliestDate(newValue)}
                    minDate={dayjs()}
                    ampm={false}
                    minutesStep={1}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <DateTimePicker
                    label="Latest completion date & time"
                    value={timeEstLatestDate}
                    onChange={(newValue) => {
                      // Set minutes to 59 by default for latest completion date
                      if (newValue) {
                        setTimeEstLatestDate(newValue.minute(59).second(0));
                      } else {
                        setTimeEstLatestDate(null);
                      }
                    }}
                    minDate={timeEstEarliestDate || dayjs()}
                    ampm={false}
                    minutesStep={1}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  onClick={handleSaveTimeEstimate}
                  disabled={savingTimeEst}
                >
                  {savingTimeEst ? 'Saving...' : 'Save time estimate'}
                </Button>
              </Grid>
            </Grid>
          </LocalizationProvider>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Set completion date range. A message will be automatically added to the discussion.
          </Typography>
        </Paper>
      )}

      {/* Messages/Chat with inline attachments */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Discussion</Typography>
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1
          }}
        >
          {getTimelineItems().map((item) => {
            if (item.type === 'message') {
              const messageData = item.data;
              const authorPicture = messageData.author_picture || item.author_picture;
              const isOwnMessage = !item.isInitial && currentUserId && messageData.user_id === currentUserId;
              const isEditing = editingMessageId === messageData.id;
              
              return (
                <Box key={item.id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <Avatar 
                      src={authorPicture} 
                      sx={{ 
                        bgcolor: authorPicture ? 'transparent' : (item.isInitial ? 'primary.main' : 'secondary.main'), 
                        mr: 1 
                      }}
                    >
                      {!authorPicture && (item.author_name || 'U')[0].toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {item.author_name || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {formatDate(item.created_at)}
                          {messageData.edited_at && ' (edited)'}
                        </Typography>
                        {isOwnMessage && !isEditing && (
                          <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditMessage(messageData)}
                              sx={{ p: 0.5 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteMessage(messageData.id)}
                              sx={{ p: 0.5 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {isEditing ? (
                        <Box>
                          <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={editingMessageText}
                            onChange={(e) => setEditingMessageText(e.target.value)}
                            sx={{ mb: 1 }}
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={handleSaveEdit}
                              color="primary"
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCancelEdit}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {messageData.message}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            } else if (item.type === 'attachment') {
              const attachment = item.data;
              const attachmentAuthor = attachment.author_name || 'Unknown';
              const attachmentPicture = attachment.author_picture;
              const isImage = isImageFile(attachment.mime_type, attachment.filename);
              const imageUrl = isImage ? getImageUrl(attachment.id) : null;
              
              return (
                <Box key={item.id} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <Avatar 
                      src={attachmentPicture} 
                      sx={{ 
                        bgcolor: attachmentPicture ? 'transparent' : 'info.main', 
                        mr: 1 
                      }}
                    >
                      {!attachmentPicture && <AttachFileIcon fontSize="small" />}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {attachmentAuthor}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          {formatDate(item.created_at)}
                        </Typography>
                      </Box>
                      {isImage ? (
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            mt: 1, 
                            cursor: 'pointer',
                            '&:hover': { boxShadow: 3 }
                          }}
                          onClick={() => handleImageClick(attachment.id, attachment.filename)}
                        >
                          <Box sx={{ position: 'relative' }}>
                            <CardMedia
                              component="img"
                              image={imageUrl + '?t=' + Date.now()}
                              alt={attachment.filename}
                              onError={(e) => {
                                // Fallback if image fails to load
                                e.target.style.display = 'none';
                              }}
                              sx={{ 
                                maxHeight: 200,
                                maxWidth: '100%',
                                objectFit: 'contain',
                                bgcolor: 'grey.100'
                              }}
                            />
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                borderRadius: '50%',
                                p: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              <ZoomInIcon sx={{ color: 'white', fontSize: 20 }} />
                            </Box>
                          </Box>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                  {attachment.filename}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(attachment.file_size)}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadAttachment(attachment.id, attachment.filename);
                                }}
                                sx={{ ml: 1 }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card variant="outlined" sx={{ mt: 1 }}>
                          <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                  {attachment.filename}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatFileSize(attachment.file_size)}
                                </Typography>
                              </Box>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadAttachment(attachment.id, attachment.filename)}
                                sx={{ ml: 1 }}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            }
            return null;
          })}
          <div ref={messagesEndRef} />
        </Box>

        {/* New message input */}
        <Box>
          <TextField
            inputRef={messageInputRef}
            fullWidth
            multiline
            rows={3}
            placeholder="Type your message... (Press CTRL+V to paste images)"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onPaste={handlePaste}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleSendMessage();
              }
            }}
            sx={{ mb: 2 }}
          />
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload-detail"
            />
            <label htmlFor="file-upload-detail">
              <Button
                variant="outlined"
                component="span"
                startIcon={<AttachFileIcon />}
                size="small"
                sx={{ mr: 1 }}
              >
                Attach Files
              </Button>
            </label>
            {newFiles.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, ml: 1 }}>
                {newFiles.map((file, index) => (
                  <Chip
                    key={index}
                    label={`${file.name} (${formatFileSize(file.size)})`}
                    onDelete={() => removeFile(index)}
                    deleteIcon={<DeleteIcon />}
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Box>

          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={sending || (!newMessage.trim() && newFiles.length === 0)}
            startIcon={<SendIcon />}
          >
            {sending ? 'Sending...' : 'Send Message'}
          </Button>
        </Box>
      </Paper>

      {/* Image Preview Modal */}
      <Dialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          color: 'white',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ color: 'white' }}>
            {previewImageName}
          </Typography>
          <IconButton
            onClick={() => setImagePreviewOpen(false)}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Box
            component="img"
            src={previewImageUrl}
            alt={previewImageName}
            sx={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              cursor: 'pointer'
            }}
            onClick={() => setImagePreviewOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default TicketDetailView;
